import type { FieldConfig } from "@/utils/organizationTypeFields";

export type EmployerDetailsDocumentsRecord = Record<string, File | null>;
export type EmployerDetailsAdditionalFieldsRecord = Record<string, string>;

export interface EmployerDetailsFormValue {
  companyName: string;
  organizationNumber: string;
  organizationTypeCode: string;
  sectorCode: string;
  emailAddress: string;
  employerAddress: string;
  phoneNumber: string;
  directorName: string;
  directorEmail: string;
}

export interface EmployerDetailsFormData {
  companyName: string;
  organizationNumber: string;
  organizationTypeCode: string;
  sectorCode: string;
  emailAddress: string;
  employerAddress: string;
  phoneNumber: string;
  directorName: string;
  directorEmail: string;
  additionalData: Record<string, unknown>;
  documents: File[];
}

export type EmployerDetailsSubmitValidation =
  | {
      valid: false;
      kind: "additional_fields";
      additionalFieldErrors: Record<string, string>;
      toastMessage: string;
    }
  | {
      valid: false;
      kind: "documents";
      documentErrors: string[];
      toastMessage: string;
    }
  | {
      valid: true;
      formData: EmployerDetailsFormData;
    };

export const validateAndBuildEmployerDetailsSubmission = ({
  value,
  organizationNumberPrefix,
  dynamicFieldConfigs,
  additionalFields,
  documents,
}: {
  value: EmployerDetailsFormValue & {
    // the page uses org fields coming from the form; keep it compatible
    [key: string]: unknown;
  };
  organizationNumberPrefix: string;
  isRCSector: boolean;
  dynamicFieldConfigs: { fields: FieldConfig[]; documents: FieldConfig[] };
  additionalFields: EmployerDetailsAdditionalFieldsRecord;
  documents: EmployerDetailsDocumentsRecord;
}): EmployerDetailsSubmitValidation => {
  const fullOrganizationNumber = organizationNumberPrefix
    ? `${organizationNumberPrefix}${value.organizationNumber}`
    : value.organizationNumber;

  // Validate required additional fields.
  const requiredAdditionalFields = dynamicFieldConfigs.fields.filter(
    (field) => field.required,
  );
  const missingAdditionalFields = requiredAdditionalFields.filter((field) => {
    const raw = additionalFields[field.name];
    return !raw?.trim();
  });

  if (missingAdditionalFields.length > 0) {
    const errors: Record<string, string> = {};
    missingAdditionalFields.forEach((field) => {
      errors[field.name] = `${field.label} is required`;
    });
    return {
      valid: false,
      kind: "additional_fields",
      additionalFieldErrors: errors,
      toastMessage: `Please fill in: ${missingAdditionalFields
        .map((f) => f.label)
        .join(", ")}`,
    };
  }

  const tinValue = additionalFields.tinNumber?.trim();
  if (tinValue && tinValue.length > 13) {
    return {
      valid: false,
      kind: "additional_fields",
      additionalFieldErrors: {
        tinNumber: "TIN Number must not exceed 13 characters",
      },
      toastMessage: "TIN Number must not exceed 13 characters",
    };
  }

  // Validate required documents.
  const requiredDocs = dynamicFieldConfigs.documents.filter(
    (doc) => doc.required,
  );
  const missingDocs = requiredDocs.filter((doc) => !documents[doc.name]);

  if (missingDocs.length > 0) {
    const missingLabels = missingDocs.map((d) => d.label);
    return {
      valid: false,
      kind: "documents",
      documentErrors: missingLabels,
      toastMessage: `Please upload: ${missingLabels.join(", ")}`,
    };
  }

  const additionalData: Record<string, unknown> = {
    ...additionalFields,
    companyRegistrationNumber: fullOrganizationNumber,
    currentValidAddress: value.employerAddress,
  };

  const documentsArray = Object.values(documents).filter(
    (file): file is File => file !== null,
  );

  return {
    valid: true,
    formData: {
      companyName: value.companyName,
      organizationNumber: fullOrganizationNumber,
      organizationTypeCode: value.organizationTypeCode,
      sectorCode: value.sectorCode,
      emailAddress: value.emailAddress.trim(),
      employerAddress: value.employerAddress,
      phoneNumber: value.phoneNumber,
      directorName: value.directorName,
      directorEmail: value.directorEmail.trim(),
      additionalData,
      documents: documentsArray,
    },
  };
};
